import { hasConsentedToSocialMediaConversion } from "common/utils/ui/iub-consent-utils";
import { notifyError } from "common/utils/app-monitoring/notify";

// MN-5861: Endpoint is the same path declared at
// `resources :social_media_conversion_api do post 'event' on: :collection end`
// in config/routes.rb. CSRF is not required (controller has
// `protect_from_forgery except: :event`); the endpoint is unauthenticated
// and validates the Lead event_id format + content_category allowlist
// server-side.
const SOCIAL_MEDIA_CONVERSION_API_EVENT_PATH = "/api/social_media_conversion_api/event";

const getReferrerUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const returnUrl = urlParams.get("return_url");

  if (returnUrl) {
    return returnUrl;
  }

  if (document.referrer && document.referrer !== window.location.href) {
    return document.referrer;
  }

  return "/";
};

const hideLoader = () => {
  const loader = document.getElementById("wholesale-inquiry-loader");
  if (loader) {
    loader.style.display = "none";
  }
};

const initWholesaleInquiryLoader = () => {
  window.addEventListener("hs-form-event:on-ready", hideLoader);
};

const initWholesaleInquiryRedirect = () => {
  window.addEventListener("hs-form-event:on-submission:success", () => {
    const returnUrl = getReferrerUrl();
    window.location.replace(returnUrl);
  });
};

// MN-5861: Resolve the HubSpot embed origin from the server-rendered URL
// (Settings.hubspot_forms_base_url) so the postMessage origin guard tracks
// the configured embed source instead of being hard-coded.
const resolveHubspotOrigin = rawUrl => {
  if (!rawUrl) return null;
  try {
    return new URL(rawUrl).origin;
  } catch (_e) {
    return null;
  }
};

// MN-5861: Browser-fire a Meta `Lead` event when the wholesale-inquiry
// HubSpot form is submitted. The browser fbq fire and the server CAPI fire
// share the same event_id (LEAD-<uuid>) so Meta deduplicates them. Both legs
// are gated by the same Flip — the server renders its value into the
// container's `data-meta-capi-lead-enabled` attribute so flip-off silences
// the browser leg too without a redeploy.
//
// `leadListenerAttached` is intentionally module-scoped: the wholesale-inquiry
// page mounts the form once, so a second `initWholesaleInquiry()` call (e.g.
// during a hot-reload in dev, or hypothetical second mount) is treated as a
// no-op rather than re-registering the window-level `message` listener and
// double-firing the Lead event. If a future caller legitimately mounts the
// form twice on the same page, this guard becomes a silent miss — at that
// point convert it to a per-container `data-listener-attached` attribute.
let leadListenerAttached = false;

const isWholesaleFormSubmitted = (event, allowedOrigin, expectedFormId) => {
  // Origin guard — postMessage is otherwise spoofable by any iframe.
  if (event.origin !== allowedOrigin) return false;
  const { data } = event;
  if (!data || typeof data !== "object") return false;
  if (data.type !== "hsFormCallback") return false;
  // Use `onFormSubmitted` (post-server-ack), not `onFormSubmit` which
  // fires before HubSpot persists the lead and on validation failure.
  if (data.eventName !== "onFormSubmitted") return false;
  // Match the specific wholesale form so a future second HubSpot embed on
  // the same page can't trigger a Lead event.
  return data.id === expectedFormId;
};

const fireLeadEvent = () => {
  if (!hasConsentedToSocialMediaConversion()) return;

  const uuid = window.crypto?.randomUUID?.();
  if (!uuid) return; // Browsers without crypto.randomUUID skip silently.
  const eventId = `LEAD-${uuid}`;

  if (typeof window.fbq === "function") {
    window.fbq("track", "Lead", { content_category: "wholesale_inquiry" }, { eventID: eventId });
  }

  // Use navigator.sendBeacon directly so the POST queues even if the page
  // navigates away — the existing `hs-form-event:on-submission:success`
  // listener calls `window.location.replace` on the same submission and
  // would otherwise race / abort an in-flight fetch.
  //
  // We bypass the shared `api.product.*` helper here on purpose: it wraps
  // the payload as `{ body: <jsonString>, sendBeacon: true }` before
  // delegating to the request layer, which means the beacon body is the
  // wrapped envelope rather than top-level params, and Rails would never
  // see `params[:event_name] == 'Lead'`. Sending a JSON Blob directly
  // makes the controller's standard JSON param parsing populate the keys
  // we expect.
  try {
    const payload = {
      event_name: "Lead",
      event_id: eventId,
      content_category: "wholesale_inquiry",
      source_url: window.location.href
    };
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    const queued = navigator.sendBeacon?.(SOCIAL_MEDIA_CONVERSION_API_EVENT_PATH, blob);
    if (!queued) {
      notifyError("Lead conversion beacon failed to queue", {
        context: { event_id: eventId }
      });
    }
  } catch (error) {
    notifyError("Error sending Lead conversion event", { context: { error } });
  }
};

const initWholesaleInquiryLeadEvent = formContainer => {
  if (leadListenerAttached) return;

  const enabled = formContainer.dataset.metaCapiLeadEnabled === "true";
  if (!enabled) return;

  const expectedFormId = formContainer.dataset.hubspotFormId;
  const allowedOrigin = resolveHubspotOrigin(formContainer.dataset.hubspotFormsBaseUrl);
  if (!expectedFormId || !allowedOrigin) return;

  leadListenerAttached = true;

  window.addEventListener("message", event => {
    if (!isWholesaleFormSubmitted(event, allowedOrigin, expectedFormId)) return;
    fireLeadEvent();
  });
};

const initWholesaleInquiry = () => {
  const formContainer = document.querySelector(".wholesale-inquiry-container");
  if (!formContainer) {
    return;
  }

  initWholesaleInquiryLoader();
  initWholesaleInquiryRedirect();
  initWholesaleInquiryLeadEvent(formContainer);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initWholesaleInquiry);
} else {
  initWholesaleInquiry();
}

export default initWholesaleInquiry;
